import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Bell, BellRing, Lock, RefreshCw, Rss, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useConnectedAccounts } from '@/hooks/useConnectedAccounts';
import { useOAuthFlow } from '@/hooks/useOAuthFlow';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getPlatformById, type SocialPlatform } from '@/lib/social-platforms';
import { OAUTH_PLATFORMS, getPlatformDisplayName } from '@/lib/utils';
import { BlueskyConnectForm } from '@/components/platform/BlueskyConnectForm';
import { UpgradeModal } from '@/components/dashboard/UpgradeModal';
import { RssSourcePicker } from '@/components/dashboard/RssSourcePicker';

// Special listening-rule name used to persist the user's global RSS subscriptions.
// The rule itself has no keywords — it just ensures these feeds are fetched.
const RSS_RULE_NAME = 'My Publications';

const ACTIVE_IDS = ['bluesky', 'youtube', 'reddit', 'twitter'] as const;

// Platforms that require a paid tier to connect
const PAID_PLATFORM_IDS = new Set(['reddit', 'twitter']);

// Coming-soon entries are hardcoded here so we don't have to extend
// social-platforms.ts (which is treated as a stable source of truth).
interface ComingSoonEntry {
  id: string;
  name: string;
  description: string;
}
const COMING_SOON: ComingSoonEntry[] = [
  { id: 'rss', name: 'RSS / Newsletters', description: 'Subscribe to any Substack, blog, or publication by URL.' },
  { id: 'hackernews', name: 'Hacker News', description: 'Top stories, Ask HN, and Show HN in your feed.' },
  { id: 'mastodon', name: 'Mastodon', description: 'Connect your Mastodon account from any instance.' },
  { id: 'threads', name: 'Threads', description: 'Via ActivityPub — same open protocol as Mastodon.' },
];

const NOTIFY_STORAGE_KEY = 'platform_notify_optins';

function loadNotifyOptIns(): Set<string> {
  try {
    const raw = localStorage.getItem(NOTIFY_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveNotifyOptIns(set: Set<string>) {
  try {
    localStorage.setItem(NOTIFY_STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    // localStorage can throw in private mode; non-fatal
  }
}

export function Platforms() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { accounts, refreshAccounts, disconnectAccount, loading } = useConnectedAccounts();
  const { connectPlatform, isConnecting } = useOAuthFlow({ onSuccess: refreshAccounts });
  const [showBluesky, setShowBluesky] = useState(false);
  const [notifyOptIns, setNotifyOptIns] = useState<Set<string>>(() => loadNotifyOptIns());
  const [gatedPlatform, setGatedPlatform] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [paidUnlocked, setPaidUnlocked] = useState(false);
  const [showXAccountPicker, setShowXAccountPicker] = useState(false);
  const oauthHandledRef = useRef(false);

  // RSS subscriptions — backed by a special listening rule named "My Publications"
  const [rssRuleId, setRssRuleId] = useState<string | null>(null);
  const [rssFeeds, setRssFeeds] = useState<string[]>([]);
  const [rssSaving, setRssSaving] = useState(false);

  // Free users can see Bluesky/YouTube. Reddit and X are paid-tier only during beta;
  // founding members and Personal/Family subscribers bypass the gate.
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('profiles')
      .select('subscription_tier, is_founding_member')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        const tier = data?.subscription_tier ?? 'free';
        const founding = Boolean(data?.is_founding_member);
        setPaidUnlocked(tier !== 'free' || founding);
      });
  }, [user?.id]);

  // Load the user's saved RSS subscriptions from the special "My Publications" rule
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('filtering_rules')
      .select('id, rss_feeds')
      .eq('user_id', user.id)
      .eq('name', RSS_RULE_NAME)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setRssRuleId(data.id);
          setRssFeeds((data.rss_feeds ?? []) as string[]);
        }
      });
  }, [user?.id]);

  const handleSaveRss = async () => {
    if (!user?.id) return;
    setRssSaving(true);
    try {
      if (rssRuleId) {
        // Update existing rule
        const { error } = await supabase
          .from('filtering_rules')
          .update({ rss_feeds: rssFeeds.length ? rssFeeds : null, is_active: true } as any)
          .eq('id', rssRuleId);
        if (error) throw error;
      } else {
        // Create the special rule
        const payload: any = {
          user_id: user.id,
          name: RSS_RULE_NAME,
          rule_type: 'listening',
          keywords: null,
          platforms: ['rss'],
          rss_feeds: rssFeeds.length ? rssFeeds : null,
          is_active: true,
          intensity_gauge: 3,
          highlight_style: { badge: true },
        };
        const { data, error } = await supabase
          .from('filtering_rules')
          .insert(payload)
          .select('id')
          .single();
        if (error) throw error;
        setRssRuleId(data.id);
      }
      toast.success('RSS subscriptions saved');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save RSS subscriptions');
    } finally {
      setRssSaving(false);
    }
  };

  // Handle return from Stripe checkout (?upgraded=true)
  useEffect(() => {
    if (!searchParams.get('upgraded')) return;
    // Sync subscription status then refresh the gate
    supabase.functions.invoke('check-subscription').then(({ data }) => {
      if (data?.subscribed) {
        setPaidUnlocked(true);
        toast.success('You\'re all set! Connect Reddit or X below.');
      }
    });
    const next = new URLSearchParams(searchParams);
    next.delete('upgraded');
    next.delete('tier');
    setSearchParams(next, { replace: true });
  }, []);

  // Handle return from oauth-callback: ?connected=<platform>&username=<...>
  // The realtime subscription on useConnectedAccounts only fires while the
  // page is mounted, but the user was on Reddit/Google during the INSERT —
  // so we have to refetch ourselves on mount.
  useEffect(() => {
    if (oauthHandledRef.current) return;
    const connected = searchParams.get('connected');
    const username = searchParams.get('username');
    const error = searchParams.get('error');
    if (!connected && !error) return;
    oauthHandledRef.current = true;

    if (connected) {
      const display = username
        ? connected === 'reddit'
          ? `u/${username}`
          : connected === 'bluesky'
            ? username
            : `@${username}`
        : 'your account';
      toast.success(`${getPlatformDisplayName(connected)} connected`, {
        description: `Connected ${display}.`,
      });
      // Two ticks: immediate (in case the row landed before mount) and a
      // short backup in case of replication lag.
      refreshAccounts();
      const t = setTimeout(() => refreshAccounts(), 1500);
      // Strip the params so a refresh of the page doesn't re-toast.
      const next = new URLSearchParams(searchParams);
      next.delete('connected');
      next.delete('username');
      setSearchParams(next, { replace: true });
      return () => clearTimeout(t);
    }
    if (error) {
      toast.error('Connection failed', { description: error });
      const next = new URLSearchParams(searchParams);
      next.delete('error');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshAccounts]);

  const connectedAccounts = useMemo(
    () =>
      accounts.filter(
        (a) =>
          a.is_active &&
          a.account_username &&
          a.account_username.trim() !== '' &&
          !a.account_username.startsWith('temp_'),
      ),
    [accounts],
  );

  const accountFor = (platformId: string) =>
    connectedAccounts.find((a) => a.platform === platformId);

  const handleConnect = (platformId: string) => {
    if (platformId === 'bluesky') {
      setShowBluesky(true);
      return;
    }
    // For X, show an account-picker dialog so the user can switch accounts first
    if (platformId === 'twitter') {
      setShowXAccountPicker(true);
      return;
    }
    if (platformId in OAUTH_PLATFORMS) {
      const p = OAUTH_PLATFORMS[platformId as keyof typeof OAUTH_PLATFORMS];
      if (p !== 'bluesky') connectPlatform(p);
    }
  };

  const proceedWithXConnect = () => {
    setShowXAccountPicker(false);
    connectPlatform('twitter');
  };

  const handleDisconnect = async (accountId: string, platform: string) => {
    try {
      await disconnectAccount(accountId, platform);
      toast.success(`${getPlatformDisplayName(platform)} disconnected`);
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const handleNotifyMe = (platformId: string, platformName: string) => {
    const next = new Set(notifyOptIns);
    if (next.has(platformId)) return;
    next.add(platformId);
    setNotifyOptIns(next);
    saveNotifyOptIns(next);
    toast.success(`We'll let you know when ${platformName} is ready`);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-44 bg-muted rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const activePlatforms: SocialPlatform[] = ACTIVE_IDS
    .map((id) => getPlatformById(id))
    .filter((p): p is SocialPlatform => Boolean(p));

  return (
    <div className="p-6 space-y-10">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Platform Connections</h1>
          <p className="text-muted-foreground mt-1">
            Connect your social media accounts to start monitoring
          </p>
        </div>
        <Button onClick={refreshAccounts} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Free-tier upgrade nudge — only shown to users who haven't subscribed */}
      {!paidUnlocked && (
        <div className="rounded-xl border border-[#1A2E4A]/20 bg-[#1A2E4A]/[0.03] px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#1A2E4A] text-sm">Unlock Reddit &amp; X (Twitter)</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Reddit and X unlock with a paid plan. Start for $1 — scales with what you use.
            </p>
          </div>
          <Button
            size="sm"
            className="shrink-0 bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white"
            onClick={() => { setGatedPlatform(null); setShowUpgrade(true); }}
          >
            See plans →
          </Button>
        </div>
      )}

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Available now</h2>
          <p className="text-sm text-muted-foreground">
            Live integrations you can connect today.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activePlatforms.map((platform) => {
            const account = accountFor(platform.id);
            const isConnected = Boolean(account);
            const Icon = platform.icon;
            const loadingThis = isConnecting[platform.id];

            return (
              <Card key={platform.id} className="p-6 hover:shadow-md transition-shadow">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-muted">
                        <Icon className="h-5 w-5 text-foreground/70" />
                      </div>
                      <h3 className="font-semibold text-lg">{platform.name}</h3>
                    </div>
                    {isConnected ? (
                      <Badge className="bg-green-100 text-green-800 border-green-200">
                        Connected
                      </Badge>
                    ) : PAID_PLATFORM_IDS.has(platform.id) && !paidUnlocked ? (
                      <Badge className="bg-[#1A2E4A]/10 text-[#1A2E4A] border-[#1A2E4A]/20">
                        <Lock className="h-3 w-3 mr-1" />
                        Paid plan
                      </Badge>
                    ) : (
                      <Badge variant="outline">Free</Badge>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground min-h-[2.5rem]">
                    {platform.description}
                  </p>

                  {isConnected && account ? (
                    <div className="space-y-3">
                      <div className="text-sm">
                        <span className="font-medium">{account.account_username}</span>
                        <span className="text-muted-foreground ml-2 text-xs">
                          Connected {new Date(account.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleDisconnect(account.id, account.platform)}
                      >
                        Disconnect
                      </Button>
                    </div>
                  ) : PAID_PLATFORM_IDS.has(platform.id) && !paidUnlocked ? (
                    <Button
                      className="w-full bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white"
                      onClick={() => { setGatedPlatform(platform.id); setShowUpgrade(true); }}
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      Upgrade to Connect
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      onClick={() => handleConnect(platform.id)}
                      disabled={loadingThis}
                    >
                      {loadingThis ? 'Connecting...' : `Connect ${platform.name}`}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Coming soon</h2>
          <p className="text-sm text-muted-foreground">
            Tap "Notify me" and we'll let you know when these go live.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {COMING_SOON.map((platform) => {
            const optedIn = notifyOptIns.has(platform.id);
            return (
              <Card
                key={platform.id}
                className="p-6 opacity-70 hover:opacity-90 transition-opacity"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg text-muted-foreground">
                      {platform.name}
                    </h3>
                    <Badge variant="secondary">Coming soon</Badge>
                  </div>

                  <p className="text-sm text-muted-foreground min-h-[2.5rem]">
                    {platform.description}
                  </p>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleNotifyMe(platform.id, platform.name)}
                    disabled={optedIn}
                  >
                    {optedIn ? (
                      <>
                        <BellRing className="h-4 w-4 mr-2" />
                        Notified
                      </>
                    ) : (
                      <>
                        <Bell className="h-4 w-4 mr-2" />
                        Notify me
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ── RSS & Publications ──────────────────────────────────────────── */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Rss className="h-5 w-5 text-orange-500" />
            RSS &amp; Publications
          </h2>
          <p className="text-sm text-muted-foreground">
            Subscribe to any news source or publication. Articles appear alongside your social feeds.
            You can also paste any custom RSS/Atom URL.
          </p>
        </div>
        <Card className="p-6">
          <RssSourcePicker value={rssFeeds} onChange={setRssFeeds} />
          <div className="mt-4 flex items-center justify-between border-t pt-4">
            <p className="text-xs text-muted-foreground">
              {rssFeeds.length === 0
                ? 'No publications selected yet.'
                : `${rssFeeds.length} publication${rssFeeds.length !== 1 ? 's' : ''} selected.`}
            </p>
            <Button onClick={handleSaveRss} disabled={rssSaving} size="sm">
              <Save className="h-4 w-4 mr-2" />
              {rssSaving ? 'Saving…' : 'Save subscriptions'}
            </Button>
          </div>
        </Card>
      </section>

      <UpgradeModal
        open={showUpgrade}
        onClose={() => { setShowUpgrade(false); setGatedPlatform(null); }}
        triggeredBy={
          gatedPlatform === 'twitter' ? 'X (Twitter)' :
          gatedPlatform === 'reddit' ? 'Reddit' : undefined
        }
      />

      <Dialog open={showBluesky} onOpenChange={setShowBluesky}>
        <DialogContent className="p-0 border-0 bg-transparent shadow-none max-w-md">
          <BlueskyConnectForm
            onSuccess={() => {
              setShowBluesky(false);
              refreshAccounts();
            }}
            onCancel={() => setShowBluesky(false)}
          />
        </DialogContent>
      </Dialog>

      {/* X account picker — two-step: log in to right account first, then connect */}
      <Dialog open={showXAccountPicker} onOpenChange={setShowXAccountPicker}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Connect your X account</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-4 text-sm text-muted-foreground pt-1">
                <p className="text-foreground font-medium">
                  Do this in order — it takes 30 seconds:
                </p>
                <ol className="space-y-3 list-none">
                  <li className="flex gap-2.5">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-[#1A2E4A]/10 text-[#1A2E4A] text-xs font-semibold flex items-center justify-center mt-0.5">1</span>
                    <div>
                      <p className="text-foreground font-medium text-sm">Switch X to the right account</p>
                      <p className="text-xs mt-0.5">Open X in a new tab, log out of any account you don't want to connect, then sign in as the account you <em>do</em> want.</p>
                      <a
                        href="https://x.com/login"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1.5 text-xs font-medium text-[#1A2E4A] underline underline-offset-2 hover:opacity-70"
                      >
                        Open X to switch accounts ↗
                      </a>
                    </div>
                  </li>
                  <li className="flex gap-2.5">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-[#1A2E4A]/10 text-[#1A2E4A] text-xs font-semibold flex items-center justify-center mt-0.5">2</span>
                    <div>
                      <p className="text-foreground font-medium text-sm">Come back here and connect</p>
                      <p className="text-xs mt-0.5">Once you're logged into the right account on X, click the button below. X will confirm which account to connect.</p>
                    </div>
                  </li>
                </ol>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-3">
            <Button
              className="w-full bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white"
              onClick={proceedWithXConnect}
              disabled={isConnecting['twitter']}
            >
              {isConnecting['twitter'] ? 'Connecting…' : "I'm logged in — connect now →"}
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowXAccountPicker(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Platforms;
